import { __awaiter } from "tslib";
import { JetstreamWebsocketStatusCode } from "../types";
import Api from "./api";
import { log, retrieveUserAgentInformation } from "../utils";
import { getGlobalWebrtcPlayer, removeGlobalWebrtcPlayer } from "../globals";
export default class Controller {
    constructor(configService, cirrusApi, aveApi, matchmakerApi, timeoutTrackingService, datachannelHandler, httpClient) {
        this.configService = configService;
        this.cirrusApi = cirrusApi;
        this.aveApi = aveApi;
        this.matchmakerApi = matchmakerApi;
        this.timeoutTrackingService = timeoutTrackingService;
        this.api = new Api(this.aveApi, cirrusApi);
        this.datachannelHandler = datachannelHandler;
        this.httpClient = httpClient;
    }
    connectWithClientConfig(container, clientConfig, jetstreamOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            this.validateClientConfig(clientConfig);
            if (this.cirrusApi.isConnectionActive()) {
                return;
            }
            this.cirrusApi.setConnectionActive();
            this.container = container;
            yield this.initializeConnection(clientConfig, jetstreamOptions);
        });
    }
    connectWithSessionToken(container, sessionToken, jetstreamOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!sessionToken) {
                throw new Error(`Provide a session token! Got sessionToken=${sessionToken}`);
            }
            if (this.cirrusApi.isConnectionActive()) {
                return;
            }
            this.cirrusApi.setConnectionActive();
            this.container = container;
            this.setSessionToken(sessionToken);
            yield this.initializeConnection(this.clientConfig, jetstreamOptions);
        });
    }
    connectToLocalStreamingInstance(container, localStreamingInstanceUrl, jetstreamOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!localStreamingInstanceUrl) {
                throw new Error(`Provide a streaming instance URL! Got streamingInstanceUrl=${localStreamingInstanceUrl}`);
            }
            if (this.cirrusApi.isConnectionActive()) {
                return;
            }
            this.cirrusApi.setConnectionActive();
            this.container = container;
            this.configService.setLocalAveContext(localStreamingInstanceUrl);
            yield this.initializeConnection(undefined, jetstreamOptions, localStreamingInstanceUrl);
        });
    }
    onDisconnect(disconnectHandler) {
        var _a;
        this.disconnectHandler = () => {
            disconnectHandler();
            removeGlobalWebrtcPlayer(this.sessionToken);
        };
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("disconnected", () => { var _a; return (_a = this.disconnectHandler) === null || _a === void 0 ? void 0 : _a.call(this); });
    }
    onMultiSessionClose(multiSessionCloseHandler) {
        var _a;
        this.multiSessionCloseHandler = multiSessionCloseHandler;
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("disconnected", (reason, data) => {
            if (reason === "remoteRequest" && (data === null || data === void 0 ? void 0 : data.code) === JetstreamWebsocketStatusCode.TERMINATE_MULTI_SESSION) {
                multiSessionCloseHandler();
            }
        });
    }
    onSessionTimeoutClose(sessionTimeoutCloseHandler) {
        var _a;
        this.sessionTimeoutCloseHandler = sessionTimeoutCloseHandler;
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("disconnected", (reason, data) => {
            if (reason === "remoteRequest" && (data === null || data === void 0 ? void 0 : data.code) === JetstreamWebsocketStatusCode.INACTIVITY_TIMEOUT) {
                sessionTimeoutCloseHandler();
            }
        });
    }
    onWarningBeforeDisconnect(warningBeforeDisconnectHandler, secondsBeforeDisconnect = 30) {
        if (this.configService.config.inactivityTimeout < secondsBeforeDisconnect) {
            throw new Error(`secondsBeforeDisconnect=${secondsBeforeDisconnect} may not be greater than the inactivityTimeout=${this.configService.config.inactivityTimeout}`);
        }
        this.timeoutTrackingService.setupIdleTimeoutWarning(warningBeforeDisconnectHandler, secondsBeforeDisconnect);
    }
    onError(errorHandler) {
        var _a;
        this.errorHandler = errorHandler;
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("disconnected", (reason) => {
            if (reason === "error") {
                errorHandler();
            }
        });
    }
    onBroadcast(broadcastHandler) {
        this.cirrusApi.onBroadcast(broadcastHandler);
    }
    onUserInteraction(userInteractionHandler) {
        var _a;
        this.userInteractionHandler = userInteractionHandler;
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("userInteraction", userInteractionHandler);
    }
    onDatachannelMessage(aveMessageHandler) {
        var _a;
        this.aveMessageHandler = aveMessageHandler;
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("aveMessage", (aveMessage) => aveMessageHandler(aveMessage[0]));
    }
    endMultiSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.sessionToken) {
                throw new Error("Session token is not set. Cannot end multi-session.");
            }
            this.cirrusApi.sendEndMultiSession(this.sessionToken);
        });
    }
    broadcastToOtherPlayers(message) {
        return __awaiter(this, void 0, void 0, function* () {
            this.cirrusApi.sendBroadcast(message);
        });
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.removeHtmlContainer({
                htmlContainerReference: this.container instanceof HTMLElement ? this.container : undefined,
                htmlContainerId: this.container instanceof HTMLElement ? undefined : this.container,
                disconnectOnLastRemoval: true,
            });
            this.cirrusApi.close();
        });
    }
    checkWebRtcAvailability() {
        return Boolean(navigator.webkitGetUserMedia || navigator.mozGetUserMedia || window.RTCPeerConnection);
    }
    getSessionToken(clientConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            this.validateClientConfig(clientConfig);
            this.clientConfig = clientConfig;
            const sessionToken = yield this.matchmakerApi.fetchNewSessionToken(clientConfig);
            this.setSessionToken(sessionToken);
            log.debug(`Received session-token=${sessionToken} from the matchmaker`);
            return sessionToken;
        });
    }
    /**
     * Make private, but is breaking change
     */
    setSessionToken(sessionToken) {
        if (!sessionToken) {
            throw new Error(`Provide a session token! Got sessionToken=${sessionToken}`);
        }
        log.debug(`Saving session token: ${sessionToken}`);
        this.sessionToken = sessionToken;
        this.httpClient.sessionToken = sessionToken;
    }
    checkInstanceAvailability(sessionToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!sessionToken) {
                throw new Error(`Provide a session token! Got sessionToken=${sessionToken}`);
            }
            return this.matchmakerApi.checkInstanceAvailability();
        });
    }
    checkInstanceAvailabilityWithClientConfig(clientConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            this.validateClientConfig(clientConfig);
            return this.matchmakerApi.checkInstanceAvailability(clientConfig);
        });
    }
    initializeConnection(clientConfig, jetstreamOptions, localStreamingInstanceUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            const { signalingSocketUrl, sessionToken } = yield this.getSignalingSocketUrl(clientConfig, localStreamingInstanceUrl);
            this.setSessionToken(sessionToken);
            this.setupWebrtcPlayer(sessionToken);
            if (((_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.isConnected()) || ((_b = this.webrtcPlayer) === null || _b === void 0 ? void 0 : _b.isConnecting())) {
                (_c = this.webrtcPlayer) === null || _c === void 0 ? void 0 : _c.addHtmlContainer({
                    htmlContainerReference: this.container instanceof HTMLElement ? this.container : undefined,
                    htmlContainerId: this.container instanceof HTMLElement ? undefined : this.container,
                    displayMode: jetstreamOptions === null || jetstreamOptions === void 0 ? void 0 : jetstreamOptions.displayMode,
                });
            }
            else {
                const signalingUrl = this.constructSignalingUrl(signalingSocketUrl, sessionToken, clientConfig, jetstreamOptions);
                yield ((_d = this.webrtcPlayer) === null || _d === void 0 ? void 0 : _d.connect({
                    htmlContainerReference: this.container instanceof HTMLElement ? this.container : undefined,
                    htmlContainerId: this.container instanceof HTMLElement ? undefined : this.container,
                    displayMode: jetstreamOptions === null || jetstreamOptions === void 0 ? void 0 : jetstreamOptions.displayMode,
                    signalingOptions: { type: "cirrus", cirrus: { signalingUrl } },
                }));
            }
            yield this.cirrusApi.connect();
            (_e = this.webrtcPlayer) === null || _e === void 0 ? void 0 : _e.on("userInteraction", () => this.timeoutTrackingService.resetIdleTimeout());
        });
    }
    setupWebrtcPlayer(sessionToken) {
        var _a, _b, _c;
        const webrtcPlayer = getGlobalWebrtcPlayer(sessionToken);
        this.webrtcPlayer = webrtcPlayer;
        this.datachannelHandler.webrtcPlayer = webrtcPlayer;
        this.cirrusApi.webrtcPlayer = webrtcPlayer;
        // set all listeners for the webrtcPlayer
        (_a = this.webrtcPlayer) === null || _a === void 0 ? void 0 : _a.on("disconnected", (reason, data) => {
            var _a, _b, _c, _d;
            (_a = this.disconnectHandler) === null || _a === void 0 ? void 0 : _a.call(this);
            if (reason === "error") {
                (_b = this.errorHandler) === null || _b === void 0 ? void 0 : _b.call(this);
            }
            if (reason === "remoteRequest" && (data === null || data === void 0 ? void 0 : data.code) === JetstreamWebsocketStatusCode.TERMINATE_MULTI_SESSION) {
                (_c = this.multiSessionCloseHandler) === null || _c === void 0 ? void 0 : _c.call(this);
            }
            if (reason === "remoteRequest" && (data === null || data === void 0 ? void 0 : data.code) === JetstreamWebsocketStatusCode.INACTIVITY_TIMEOUT) {
                (_d = this.sessionTimeoutCloseHandler) === null || _d === void 0 ? void 0 : _d.call(this);
            }
        });
        (_b = this.webrtcPlayer) === null || _b === void 0 ? void 0 : _b.on("userInteraction", () => { var _a; return (_a = this.userInteractionHandler) === null || _a === void 0 ? void 0 : _a.call(this); });
        (_c = this.webrtcPlayer) === null || _c === void 0 ? void 0 : _c.on("aveMessage", (aveMessage) => { var _a; return (_a = this.aveMessageHandler) === null || _a === void 0 ? void 0 : _a.call(this, aveMessage[0]); });
    }
    constructSignalingUrl(webSocketUrl, sessionToken, clientConfig, jetstreamOptions) {
        const signalingUrl = new URL(webSocketUrl);
        if (sessionToken) {
            signalingUrl.searchParams.append("token", sessionToken);
        }
        if (clientConfig) {
            signalingUrl.searchParams.append("brand", clientConfig.brand);
            signalingUrl.searchParams.append("service", clientConfig.service);
            signalingUrl.searchParams.append("market", clientConfig.market);
        }
        if (jetstreamOptions === null || jetstreamOptions === void 0 ? void 0 : jetstreamOptions.skipAutomaticAveSession) {
            signalingUrl.searchParams.append("skip-ave-session", `${jetstreamOptions === null || jetstreamOptions === void 0 ? void 0 : jetstreamOptions.skipAutomaticAveSession}`);
        }
        const userAgent = retrieveUserAgentInformation();
        signalingUrl.searchParams.append("mobile", userAgent.mobile.toString());
        signalingUrl.searchParams.append("browser", userAgent.browser);
        return signalingUrl.toString();
    }
    getSignalingSocketUrl(clientConfig, streamingInstanceUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            if (streamingInstanceUrl) {
                return { signalingSocketUrl: streamingInstanceUrl.replace("http", "ws"), sessionToken: "local" };
            }
            const { aveInstanceIp, sessionToken } = yield this.matchmakerApi.requestAveInstance(clientConfig);
            this.cirrusApi.setCirrusIp(aveInstanceIp);
            return { signalingSocketUrl: `wss://${aveInstanceIp}`, sessionToken };
        });
    }
    validateClientConfig(clientConfig) {
        if (!clientConfig || !clientConfig.service || !clientConfig.market) {
            throw new Error(`Please provide a valid client configuration. Got clientConfig=${JSON.stringify(clientConfig)}`);
        }
    }
}
//# sourceMappingURL=controller.js.map